import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ReactDatetime from 'react-datetime';
import moment from 'moment';

import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import RRule, { rrulestr, Weekday } from 'rrule';
import Select from 'react-select';
import { DropdownButton, Dropdown } from 'react-bootstrap';

import ApiCaller from 'lib/ApiCaller';
import { AppState } from 'store/store';
import QuestionApi from 'api/QuestionApi';
import ExamScheduleCollection from 'lib/common/models/examScheduleCollection';
import ExamScheduleApi from 'api/ExamScheduleApi';
import ReactTable from 'components/ReactTable/ReactTable';
import ExamSchedule, {
  ExamScheduleTypes,
  ExamScheduleTypesLabels,
} from 'lib/common/models/examSchedule';
import Syllabus from 'lib/common/models/syllabus';
import FilteredDateComponent from '../../../components/common/filteredDateComponent';
import Module from 'lib/common/models/module';

import 'react-toastify/dist/ReactToastify.css';
import { useSyllabus } from 'hooks';

const NavigationExamSettings: React.FC = () => {
  const loggedUser = useSelector((state: AppState) => state.session.userInfo);

  const { syllabus } = useSyllabus();
  const [modules, setModules] = useState<any[]>([]);
  const [syllabusId, setSyllabusId] = React.useState<Syllabus>();
  const [moduleId, setModuleId] = React.useState<Module>();
  const [schedules, setSchedules] = React.useState<ExamScheduleCollection>(
    new ExamScheduleCollection([]),
  );
  const [date, setDate] = React.useState(new Date());
  const [calendarDate, setCalendarDate] = React.useState(new Date());
  const [ruleType, setRuleType] = React.useState(
    ExamScheduleTypesLabels.ADD_DAY,
  );
  const [weeklyForm, setWeeklyForm] = React.useState({
    dateStart: moment().toDate(),
    dateEnd: null,
    byWeekDay: [RRule.MO],
    byMonthDay: [],
    byMonthDayStr: '',
  });
  const [timeSlots, setTimeSlots] = React.useState<string[]>([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = React.useState<string[]>(
    [],
  );

  const [noDatesAvailable, setNoDatesAvailable] =
    React.useState<boolean>(false);
  const [showPastSchedules, setShowPastSchedules] =
    React.useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [editingSchedule, setEditingSchedule] = React.useState<ExamSchedule | null>(null);
  const [calendarRefreshKey, setCalendarRefreshKey] = React.useState<number>(0);

  React.useEffect(() => {
    setTimeSlots(getTimeSlots());
  }, []);

  React.useEffect(() => {
    if (moduleId?._id) {
      const scheduleApi = new ExamScheduleApi(new ApiCaller(loggedUser.token));
      scheduleApi
        .getScheduleByModule(moduleId?._id)
        .then((scheduleCollection: ExamScheduleCollection) => {
          setSchedules(scheduleCollection);
        });
    }
  }, [moduleId]);

  const onSyllabusChange = (selectedSyllabus: any) => {
    setSyllabusId(selectedSyllabus);
    const questionApi = new QuestionApi(new ApiCaller(loggedUser.token));
    questionApi.getModulesFromSyllabus(selectedSyllabus._id).then((m: any) => {
      setModules(m.modules);
    });
  };

  const onModuleChange = (selectedModule: any) => {
    setModuleId(selectedModule);
  };

  const onDateChange = (e: any) => {
    setDate(e.toDate());
  };

  const onFormDateChange = (e: any, fieldName: string) => {
    setWeeklyForm({
      ...weeklyForm,
      [fieldName]: e.toDate(),
    });
  };

  const onWeekDayChange = (e: any, weekDay: Weekday) => {
    const checked = e.target.checked;
    const weekDayNum = weekDay instanceof Weekday ? weekDay.weekday : weekDay;
    
    if (checked) {
      // Add if not already present
      const exists = weeklyForm.byWeekDay.some((wd: Weekday) => {
        const wdNum = wd instanceof Weekday ? wd.weekday : wd;
        return wdNum === weekDayNum;
      });
      if (!exists) {
        setWeeklyForm({
          ...weeklyForm,
          byWeekDay: [...weeklyForm.byWeekDay, weekDay],
        });
      }
    } else {
      // Remove by comparing weekday numbers
      const filtered = weeklyForm.byWeekDay.filter((wd: Weekday) => {
        const wdNum = wd instanceof Weekday ? wd.weekday : wd;
        return wdNum !== weekDayNum;
      });
    setWeeklyForm({
      ...weeklyForm,
        byWeekDay: filtered,
    });
    }
  };

  const onMonthDayChange = (e: any) => {
    let val = e.target.value;

    if (!Array.isArray(val)) {
      val = val.split(/[,\s]+/);
    }
    val = val.filter((v: any) => v);
    const res = val.map((n: any) => parseInt(n, 10));

    setWeeklyForm({
      ...weeklyForm,
      byMonthDayStr: e.target.value,
      byMonthDay: res,
    });
  };

  const onRuleChange = (e: any) => {
    setRuleType(e.value);
  };

  const addRecurring = () => {
    const dateEnd = weeklyForm.dateEnd
      ? weeklyForm.dateEnd
      : new Date(
          weeklyForm.dateStart.getFullYear() + 1,
          weeklyForm.dateStart.getMonth(),
          weeklyForm.dateStart.getDate(),
        );

    const vals =
      ruleType == ExamScheduleTypesLabels.MONTHLY
        ? {
            freq: RRule.MONTHLY,
            dtstart: weeklyForm.dateStart,
            until: dateEnd,
            bymonthday: weeklyForm.byMonthDay,
          }
        : {
            freq: RRule.WEEKLY,
            dtstart: weeklyForm.dateStart,
            until: dateEnd,
            byweekday: weeklyForm.byWeekDay,
          };

    const rule = new RRule(vals);
    createSchedule(rule);
  };

  const addDate = () => {
    if (date !== null) {
      const rule = new RRule({
        freq: RRule.YEARLY,
        count: 1,
        dtstart: date,
      });
      createSchedule(rule);
    }
  };

  const openCreateModal = () => {
    setEditingSchedule(null);
    // Reset form to defaults
    setRuleType(ExamScheduleTypesLabels.ADD_DAY);
    setDate(new Date());
    setWeeklyForm({
      dateStart: moment().toDate(),
      dateEnd: null,
      byWeekDay: [RRule.MO],
      byMonthDay: [],
      byMonthDayStr: '',
    });
    setSelectedTimeSlots([]);
    setIsModalOpen(true);
  };

  const openEditModal = (schedule: ExamSchedule) => {
    setEditingSchedule(schedule);
    
    // Parse the RRule to populate form
    const rule = rrulestr(schedule.rrule);
    const options = rule.options;
    
    // Set time slots
    setSelectedTimeSlots(schedule.timeSlots);
    
    // Determine rule type and populate form
    if (options.freq === RRule.YEARLY && options.count === 1) {
      setRuleType(ExamScheduleTypesLabels.ADD_DAY);
      setDate(options.dtstart || new Date());
    } else if (options.freq === RRule.WEEKLY) {
      setRuleType(ExamScheduleTypesLabels.WEEKLY);
      let byWeekDay: Weekday[] = [RRule.MO];
      if (options.byweekday) {
        // Convert to Weekday array - handle both number[] and Weekday[]
        const weekdayArray = Array.isArray(options.byweekday) 
          ? options.byweekday 
          : [options.byweekday];
        byWeekDay = weekdayArray.map((wd: any) => {
          if (typeof wd === 'number') {
            return new Weekday(wd);
          } else if (wd && typeof wd === 'object' && 'weekday' in wd) {
            // Already a Weekday object
            return wd;
          } else {
            // Fallback: try to create from the value itself
            return new Weekday(wd);
          }
        }) as Weekday[];
      }
      setWeeklyForm({
        dateStart: options.dtstart || moment().toDate(),
        dateEnd: options.until || null,
        byWeekDay: byWeekDay,
        byMonthDay: [],
        byMonthDayStr: '',
      });
    } else if (options.freq === RRule.MONTHLY) {
      setRuleType(ExamScheduleTypesLabels.MONTHLY);
      const byMonthDay = options.bymonthday || [];
      setWeeklyForm({
        dateStart: options.dtstart || moment().toDate(),
        dateEnd: options.until || null,
        byWeekDay: [],
        byMonthDay: byMonthDay,
        byMonthDayStr: byMonthDay.join(', '),
      });
    }
    
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSchedule(null);
    setSelectedTimeSlots([]);
  };

  const saveSchedule = () => {
    if (!syllabusId?._id || !moduleId?._id) {
      return;
    }

    let rule: RRule;
    
    if (ruleType === ExamScheduleTypesLabels.ADD_DAY) {
      rule = new RRule({
        freq: RRule.YEARLY,
        count: 1,
        dtstart: date,
      });
    } else {
      const dateEnd = weeklyForm.dateEnd
        ? weeklyForm.dateEnd
        : new Date(
            weeklyForm.dateStart.getFullYear() + 1,
            weeklyForm.dateStart.getMonth(),
            weeklyForm.dateStart.getDate(),
          );

      if (ruleType === ExamScheduleTypesLabels.MONTHLY) {
        rule = new RRule({
          freq: RRule.MONTHLY,
          dtstart: weeklyForm.dateStart,
          until: dateEnd,
          bymonthday: weeklyForm.byMonthDay,
        });
      } else {
        rule = new RRule({
          freq: RRule.WEEKLY,
          dtstart: weeklyForm.dateStart,
          until: dateEnd,
          byweekday: weeklyForm.byWeekDay,
        });
      }
    }

    const scheduleApi = new ExamScheduleApi(new ApiCaller(loggedUser.token));
    
    if (editingSchedule) {
      // Update existing schedule
      scheduleApi
        .updateSchedule(
          editingSchedule.id!,
          syllabusId._id,
          moduleId._id,
          rule.toString(),
          selectedTimeSlots,
        )
        .then((updatedSchedule: ExamSchedule) => {
          const updatedSchedules = schedules.schedules.map((s: ExamSchedule) =>
            s.id === updatedSchedule.id ? updatedSchedule : s,
          );
          setSchedules(new ExamScheduleCollection(updatedSchedules));
          setCalendarRefreshKey((prev) => prev + 1);
          closeModal();
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      // Create new schedule
      scheduleApi
        .createSchedule(
          syllabusId._id,
          moduleId._id,
          rule.toString(),
          selectedTimeSlots,
        )
        .then((schedule: ExamSchedule) => {
          setSchedules(
            new ExamScheduleCollection([...schedules.schedules, schedule]),
          );
          setCalendarRefreshKey((prev) => prev + 1);
          closeModal();
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  const createSchedule = (rule: RRule) => {
    const scheduleApi = new ExamScheduleApi(new ApiCaller(loggedUser.token));
    scheduleApi
      .createSchedule(
        syllabusId?._id!,
        moduleId?._id!,
        rule.toString(),
        selectedTimeSlots,
      )
      .then((schedule: ExamSchedule) => {
        setSchedules(
          new ExamScheduleCollection([...schedules.schedules, schedule]),
        );
        setSelectedTimeSlots([]);
        setCalendarRefreshKey((prev) => prev + 1);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const deleteSchedule = (e: any) => {
    const schedule = e.row.original;
    const scheduleApi = new ExamScheduleApi(new ApiCaller(loggedUser.token));
    scheduleApi.deleteSchedule(schedule.id).then(() => {
      const updatedSchedules = new ExamScheduleCollection(
        schedules.schedules.filter((item: any) => item.id !== schedule.id),
      );
      setSchedules(updatedSchedules);
      setCalendarRefreshKey((prev) => prev + 1);
    });
  };

  const columns = [
    // { Header: 'RULE', accessor: 'rrule', sortable: true },
    {
      Header: 'TO TEXT',
      accessor: (d: any) => {
        const rule = rrulestr(d.rrule);
        const ruleText = rule.toText();
        const dtstart = rule.options.dtstart;
        
        if (dtstart) {
          const startDate = moment(dtstart).format('MMMM DD, YYYY');
          // Insert start date before "until" if present, otherwise append it
          if (ruleText.toLowerCase().includes('until')) {
            return ruleText.replace(/until/i, `from ${startDate} until`);
          }
          return `${ruleText} from ${startDate}`;
        }
        
        return ruleText;
      },
      sortable: true,
    },
    {
      Header: 'TIMESLOTS',
      accessor: (d: any) => d.timeSlots.join(', '),
      sortable: true,
    },
    {
      Header: 'ACTIONS',
      Cell: (row: any) => {
        const schedule = row.row.original;
        return (
        <DropdownButton variant="default" title="Actions" size="sm">
            <Dropdown.Item onClick={() => openEditModal(schedule)}>
              <i className="nc-icon nc-settings" />
              &nbsp;&nbsp;EDIT
            </Dropdown.Item>
          <Dropdown.Item onClick={() => deleteSchedule(row)}>
            <i className="nc-icon nc-simple-delete" />
            &nbsp;&nbsp;DELETE
          </Dropdown.Item>
        </DropdownButton>
        );
      },
    },
  ];

  const getTimeSlots = () => {
    const fromDate = moment().startOf('day');
    const toDate = moment().endOf('day');
    const diff = toDate.diff(fromDate, 'minutes');
    const range = [];
    const date = fromDate.toDate();
    for (let i = 0; i <= diff; i += 15) {
      const time = moment(date).add(i, 'minutes');
      range.push(time.format('HH:mm'));
    }

    return range;
  };

  const onChangeTimeSlots = (e: any) => {
    setSelectedTimeSlots(e?.map((item: any) => item.value));
  };

  const isWeekdaySelected = (weekday: Weekday): boolean => {
    return weeklyForm.byWeekDay.some((wd: Weekday) => {
      // Compare by weekday number property
      const wdNum = wd instanceof Weekday ? wd.weekday : wd;
      const weekdayNum = weekday instanceof Weekday ? weekday.weekday : weekday;
      return wdNum === weekdayNum;
    });
  };

  const hasFutureOccurrences = (schedule: ExamSchedule): boolean => {
    try {
      const rule = rrulestr(schedule.rrule);
      const now = new Date();
      // Check if there are any occurrences after now (exclusive)
      const nextOccurrence = rule.after(now, false);
      return nextOccurrence !== null;
    } catch (error) {
      // If parsing fails, include it to be safe
      return true;
    }
  };

  const filteredSchedules = React.useMemo(() => {
    if (showPastSchedules) {
      return schedules.schedules;
    }
    return schedules.schedules.filter((schedule: ExamSchedule) =>
      hasFutureOccurrences(schedule),
    );
  }, [schedules, showPastSchedules]);

  return (
    <>
      <div className="content">
        <Card>
          <Row>
            <Col md="12">
              <CardHeader>
                <CardTitle tag="h4">Setup exam timeslots</CardTitle>
              </CardHeader>
              <CardBody>
                <Row>
                  <Label sm="2">Syllabus</Label>
                  <Col sm="10">
                    <FormGroup>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="syllabus"
                        value={syllabusId}
                        onChange={onSyllabusChange}
                        options={syllabus}
                        getOptionLabel={(item) => item.code + ' ' + item.title}
                        getOptionValue={(item) => item.code}
                        placeholder="Choose a syllabus"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Label sm="2">Module</Label>
                  <Col sm="10">
                    <FormGroup>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="module"
                        onChange={onModuleChange}
                        options={modules}
                        getOptionLabel={(item) => item.code + ' ' + item.title}
                        getOptionValue={(item) => item.code}
                        placeholder="Choose a module"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Label sm="2" />
                  <Col sm="10">
                    <Button color="primary" onClick={openCreateModal} disabled={!syllabusId || !moduleId}>
                      Add New Schedule
                    </Button>
                  </Col>
                </Row>
                <Row>
                  <Label sm="2">Schedules</Label>
                  <Col sm="10">
                    <FormGroup>
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="checkbox"
                            checked={showPastSchedules}
                            onChange={(e: any) =>
                              setShowPastSchedules(e.target.checked)
                            }
                          />
                          <span className="form-check-sign" />
                          Show past schedules
                        </Label>
                      </FormGroup>
                      <ReactTable
                        data={filteredSchedules}
                        columns={columns}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Label sm="2">Calendar Preview</Label>
                  <Col sm="10">
                    <FilteredDateComponent
                      date={calendarDate}
                      setDate={setCalendarDate}
                      module={moduleId}
                      setException={setNoDatesAvailable}
                      refreshKey={calendarRefreshKey}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Col>
          </Row>
        </Card>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        toggle={closeModal} 
        size="lg" 
        className="schedule-modal"
        contentClassName="schedule-modal-content"
      >
        <ModalHeader toggle={closeModal}>
          {editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}
        </ModalHeader>
        <ModalBody className="schedule-modal-body">
          <Row>
            <Label sm="4">Scheduling Rule Type</Label>
            <Col sm="8">
                    <FormGroup>
                      <Select
                        className="react-select primary"
                        classNamePrefix="react-select"
                        name="status"
                        value={{
                          label: ExamScheduleTypes[ruleType],
                          value: ruleType,
                        }}
                        onChange={onRuleChange}
                        options={Object.keys(ExamScheduleTypes).map(function (
                          type,
                        ) {
                          return {
                            value: type,
                            label: ExamScheduleTypes[type],
                          };
                        })}
                        placeholder="Rule type"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {ruleType && (
                  <Row>
              <Label sm="4">Time Slots</Label>
              <Col sm="8">
                      <Select
                        className="react-select info"
                        classNamePrefix="react-select"
                        placeholder="Choose time slots"
                        name="multipleSelect"
                        closeMenuOnSelect={false}
                        isMulti
                        value={selectedTimeSlots?.map((item) => {
                          return {
                            label: item,
                            value: item,
                          };
                        })}
                        onChange={(value) => onChangeTimeSlots(value)}
                        options={[
                          {
                            value: '',
                            label: ' Multiple Options',
                            isDisabled: true,
                          },
                          ...timeSlots.map((item) => {
                            return {
                              label: item,
                              value: item,
                            };
                          }),
                        ]}
                      />
                    </Col>
                  </Row>
                )}
                {(ruleType == ExamScheduleTypesLabels.MONTHLY ||
                  ruleType == ExamScheduleTypesLabels.WEEKLY) && (
                  <>
                    <Row>
                <Label sm="4">Start date</Label>
                <Col sm="8">
                  <FormGroup>
                        <ReactDatetime
                          inputProps={{
                            className: 'form-control',
                            placeholder: 'Pick Start Date',
                            name: 'Start date',
                        readOnly: true,
                          }}
                          timeFormat={'HH:mm'}
                          dateFormat={'DD-MM-YYYY'}
                          onChange={(e: any) =>
                            onFormDateChange(e, 'dateStart')
                          }
                          value={moment(weeklyForm.dateStart)}
                        />
                  </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                <Label sm="4">Date end</Label>
                <Col sm="8">
                  <FormGroup>
                        <ReactDatetime
                          inputProps={{
                            className: 'form-control',
                            placeholder: 'Pick End Date',
                            name: 'End date',
                        readOnly: true,
                          }}
                          timeFormat={'HH:mm'}
                          dateFormat={'DD-MM-YYYY'}
                          onChange={(e: any) => onFormDateChange(e, 'dateEnd')}
                          value={moment(weeklyForm.dateEnd)}
                        />
                  </FormGroup>
                  <FormText color="default" tag="span">
                          The default value is 1 year duration.
                  </FormText>
                      </Col>
                    </Row>
                    {ruleType == ExamScheduleTypesLabels.WEEKLY && (
                      <Row>
                  <Label sm="4">Weekdays</Label>
                  <Col sm="8">
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.MO)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.MO)
                                }
                              />
                              <span className="form-check-sign" />
                              Monday
                            </Label>
                          </FormGroup>{' '}
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.TU)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.TU)
                                }
                              />
                              <span className="form-check-sign" />
                              Tuesday
                            </Label>
                          </FormGroup>{' '}
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.WE)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.WE)
                                }
                              />
                              <span className="form-check-sign" />
                              Wednesday
                            </Label>
                          </FormGroup>
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.TH)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.TH)
                                }
                              />
                              <span className="form-check-sign" />
                              Thursday
                            </Label>
                          </FormGroup>
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.FR)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.FR)
                                }
                              />
                              <span className="form-check-sign" />
                              Friday
                            </Label>
                          </FormGroup>
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.SA)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.SA)
                                }
                              />
                              <span className="form-check-sign" />
                              Saturday
                            </Label>
                          </FormGroup>
                          <FormGroup check inline>
                            <Label check>
                              <Input
                                type="checkbox"
                          checked={isWeekdaySelected(RRule.SU)}
                                onChange={(e: any) =>
                                  onWeekDayChange(e, RRule.SU)
                                }
                              />
                              <span className="form-check-sign" />
                              Sunday
                            </Label>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                    {ruleType == ExamScheduleTypesLabels.MONTHLY && (
                      <Row>
                  <Label sm="4">Month days</Label>
                  <Col sm="8">
                          <FormGroup>
                            <Input
                              type="text"
                              value={weeklyForm.byMonthDayStr}
                              onChange={onMonthDayChange}
                            />
                            <FormText color="default" tag="span">
                              Enter month days separated by commas.
                            </FormText>
                          </FormGroup>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
                {ruleType == ExamScheduleTypesLabels.ADD_DAY && (
                    <Row>
              <Label sm="4">Add date</Label>
              <Col sm="8">
                        <ReactDatetime
                          inputProps={{
                            className: 'form-control',
                            placeholder: 'Pick Date',
                            name: 'Date of exam',
                          }}
                          timeFormat={false}
                          dateFormat={'DD-MM-YYYY'}
                          onChange={(e: any) => onDateChange(e)}
                          value={moment(date)}
                        />
                      </Col>
                    </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={saveSchedule}
            disabled={!syllabusId || !moduleId || selectedTimeSlots.length === 0}
          >
            {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
                        </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default NavigationExamSettings;
